import { QueryClient, type Query } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import SuperJSON from 'superjson';
import { HARC_CACHE_MAX_AGE_MS } from './harcCache';
import { useDebugStore } from '../stores/debugStore';

/**
 * アプリ全体で共有する TanStack Query クライアント。
 *
 * キャッシュの一括クリア（`queryClient.clear()`）や、特定ネームスペースの
 * 個別操作（例: `queryClient.removeQueries({ queryKey: ['harc'] })`）を行う際に
 * import して使用する。
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // persister の maxAge（HARC_CACHE_MAX_AGE_MS = 24h）と整合させるため gcTime を 24h に引き上げる。
      // デフォルトの 5 分のままだと、観察者 0 になった 5 分後に in-memory から GC され、
      // その伝播で localStorage の永続化キャッシュも消えてしまい「24h 再利用」の設計と矛盾する。
      gcTime: HARC_CACHE_MAX_AGE_MS,
    },
  },
});

/** localStorage 永続化で使用するキー名 */
export const QUERY_PERSIST_KEY = 'BOOST_QUERY_CACHE';

/**
 * localStorage を使用可能かを書き込みテストで判定する。
 * プライベートモード等で無効化されている場合は undefined を返し、
 * persister が no-op で動作するようにする（アプリ起動の阻害を防ぐ）。
 */
function getAvailableLocalStorage(): Storage | undefined {
  try {
    const probeKey = '__boost_ls_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return undefined;
  }
}

// localStorage への serialize/deserialize は SuperJSON を使う。
// HARC データは Date を多く含む（WeatherForecast.datetime / WeatherRisk.date など）。
// 標準の JSON.stringify だと Date が ISO 文字列に劣化し、hydrate 後に
// `forecast.datetime.getHours()` などのメソッド呼び出しが TypeError になる。
// SuperJSON は Date / Map / Set / BigInt 等を透過的に保持する。
const persister = createSyncStoragePersister({
  storage: getAvailableLocalStorage(),
  key: QUERY_PERSIST_KEY,
  serialize: (data) => SuperJSON.stringify(data),
  deserialize: (data) => SuperJSON.parse(data),
});

/**
 * PersistQueryClientProvider に渡す永続化オプション。
 *
 * 永続化対象は **クエリキーの先頭が `'harc'` かつデータを保持しているもののみ**。
 * データ保持判定を status ではなく `data !== undefined` にしているのは、初回成功後に
 * バックグラウンド再取得だけ失敗したケースでも、最後に取得できた正常データを
 * 保持し続けるため（「24h 再利用」の設計意図を通信不安定時にも守る）。
 *
 * デバッグトグル `debugStore.harcCacheEnabled` が false のときは永続化対象から外す
 * （開発時のキャッシュ起因不具合の切り分け用）。
 */
export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: HARC_CACHE_MAX_AGE_MS,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) => {
      if (!useDebugStore.getState().harcCacheEnabled) return false;
      return query.state.data !== undefined && query.queryKey[0] === 'harc';
    },
  },
};
